'use client';

import { c as _c } from "react/compiler-runtime";
import { type RouterOutputs } from '@shared/all/domains/trpc';
import useUser from '@shared/utils/hooks/useUser';
import { Activity, Bookmark, Bug, Check, ChevronDown, Gamepad2, Lightbulb, Loader2, LogIn, LogOut, MapPin, Moon, Plus, PlusCircle, Settings, ShieldCheck, Sun, Trash2, User, UserPlus, Zap } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import useDarkMode from '../../hooks/useDarkMode';
import Avatar from './Avatar';
import Confirmation from './Confirmation';
type StoredAccount = RouterOutputs['sessions']['accounts'][number];
type Props = {
  setDropdownOpen: (value: boolean) => void;
  openNewPostModal: () => void;
  openFeatureRequestModal: () => void;
  openLogoutModal: () => void;
  openLoginModal: () => void;
  openSignupModal: () => void;
  openVerificationModal: () => void;
  openLoginForAddAccount: () => void;
  storedAccounts: StoredAccount[];
  onQuickSwitch: (sessionId: number) => void;
  onRemoveAccount: (sessionId: number) => Promise<void>;
  switchingToId: number | null;
  isAdmin?: boolean;
};
const UserDropdown = props => {
  const $ = _c(68);
  const {
    currentUser
  } = useUser();
  const {
    effectiveTheme
  } = useDarkMode();
  const [confirmSwitchId, setConfirmSwitchId] = useState(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [accountsOpen, setAccountsOpen] = useState(false);
  let t0;
  if ($[0] !== confirmSwitchId || $[1] !== props.storedAccounts) {
    let t1;
    if ($[3] !== confirmSwitchId) {
      t1 = a => a.sessionId === confirmSwitchId;
      $[3] = confirmSwitchId;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = props.storedAccounts.find(t1);
    $[0] = confirmSwitchId;
    $[1] = props.storedAccounts;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const accountToSwitch = t0;
  let t1;
  if ($[5] !== confirmRemoveId || $[6] !== props.storedAccounts) {
    let t2;
    if ($[8] !== confirmRemoveId) {
      t2 = a_0 => a_0.sessionId === confirmRemoveId;
      $[8] = confirmRemoveId;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    t1 = props.storedAccounts.find(t2);
    $[5] = confirmRemoveId;
    $[6] = props.storedAccounts;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const accountToRemove = t1;
  let t2;
  if ($[10] !== confirmRemoveId || $[11] !== props) {
    t2 = async () => {
      if (confirmRemoveId === null) {
        return;
      }
      setRemovingId(confirmRemoveId);
      await props.onRemoveAccount(confirmRemoveId);
      setRemovingId(null);
      setConfirmRemoveId(null);
    };
    $[10] = confirmRemoveId;
    $[11] = props;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const handleConfirmRemove = t2;
  let t3;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="absolute right-2.5 top-[-7px] size-3 rotate-45 border-l border-t border-ds-border-standard bg-background-primary" />;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== accountsOpen || $[15] !== currentUser.avatarKey || $[16] !== currentUser.id || $[17] !== currentUser.isAuthenticated || $[18] !== currentUser.name || $[19] !== currentUser.robohash || $[20] !== currentUser.tag || $[21] !== currentUser.username || $[22] !== props || $[23] !== removingId) {
    t4 = currentUser.isAuthenticated && <><button onClick={e => {
        e.stopPropagation();
        setAccountsOpen(_temp);
      }} aria-expanded={accountsOpen} aria-label="Switch accounts" className="flex w-full items-center gap-2.5 border-b border-ds-border-subtle px-3 py-2.5 transition-colors hover:bg-background-light"><Avatar avatarKey={currentUser.avatarKey ?? null} robohash={currentUser.robohash ?? ""} isBot={currentUser.tag === "BOT"} userId={currentUser.id} size={36} /><div className="flex min-w-0 flex-1 flex-col items-start"><span className="truncate text-sm font-semibold text-textPrimary">{currentUser.name || currentUser.username}</span><span className="truncate text-xs text-textSecondary">@{currentUser.username}</span></div><Check size={14} className="shrink-0 text-green-500" /><ChevronDown size={16} className={`shrink-0 text-textSecondary transition-transform ${accountsOpen ? "rotate-180" : ""}`} /></button>{accountsOpen && <div className="w-full border-b border-ds-border-subtle py-1">{props.storedAccounts.map(account => <div key={account.sessionId} className="flex w-full items-center gap-2.5 px-3 py-1.5 transition-colors hover:bg-background-light"><button onClick={e_0 => {
            e_0.stopPropagation();
            setConfirmSwitchId(account.sessionId);
          }} disabled={props.switchingToId !== null || removingId !== null} className="flex min-w-0 flex-1 items-center gap-2.5 disabled:opacity-50" aria-label={`Switch to ${account.user.username}`}><Avatar avatarKey={account.user.avatarKey ?? null} robohash={account.user.robohash ?? ""} isBot={account.user.tag === "BOT"} userId={account.user.id} size={28} /><div className="flex min-w-0 flex-1 flex-col items-start"><span className="truncate text-sm font-semibold text-textPrimary">{account.user.name || account.user.username}</span><span className="truncate text-xs text-textSecondary">@{account.user.username}</span></div></button>{props.switchingToId === account.sessionId ? <Loader2 size={14} className="shrink-0 animate-spin text-textSecondary" /> : <button onClick={e_1 => {
            e_1.stopPropagation();
            setConfirmRemoveId(account.sessionId);
          }} disabled={removingId !== null} className="rounded-md p-1 text-textSecondary transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50" aria-label={`Remove ${account.user.username}`}>{removingId === account.sessionId ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}</button>}</div>)}<button onClick={e_2 => {
          e_2.stopPropagation();
          props.setDropdownOpen(false);
          props.openLoginForAddAccount();
        }} className="flex w-full items-center gap-2.5 px-3 py-1.5 text-primary transition-colors hover:bg-background-light"><div className="flex size-7 items-center justify-center rounded-full border border-dashed border-primary/40"><Plus size={14} /></div><span className="text-sm font-medium">Add account</span></button></div>}</>;
    $[14] = accountsOpen;
    $[15] = currentUser.avatarKey;
    $[16] = currentUser.id;
    $[17] = currentUser.isAuthenticated;
    $[18] = currentUser.name;
    $[19] = currentUser.robohash;
    $[20] = currentUser.tag;
    $[21] = currentUser.username;
    $[22] = props;
    $[23] = removingId;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  let t5;
  if ($[25] !== currentUser.isAuthenticated) {
    t5 = currentUser.isAuthenticated && <Link className="flex w-full items-center px-0 py-2 no-underline hover:bg-background-light" href="/settings/preferences"><div className="flex w-8 justify-end"><Settings width={20} height={20} color="rgba(var(--text-color))" /></div><button className="flex whitespace-nowrap border-none bg-transparent pl-2 text-center text-base font-semibold text-textPrimary transition-all duration-200 ease-in-out">Settings</button></Link>;
    $[25] = currentUser.isAuthenticated;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Link className="flex w-full items-center px-0 py-2 no-underline hover:bg-background-light" href="/map"><div className="flex w-8 justify-end"><MapPin size={20} /></div><button className="flex whitespace-nowrap border-none bg-transparent pl-2 text-center text-base font-semibold text-textPrimary transition-all duration-200 ease-in-out">Map</button></Link>;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Link className="flex w-full items-center px-0 py-2 no-underline hover:bg-background-light" href="/games"><div className="flex w-8 justify-end"><Gamepad2 size={20} /></div><button className="flex whitespace-nowrap border-none bg-transparent pl-2 text-center text-base font-semibold text-textPrimary transition-all duration-200 ease-in-out">Games</button></Link>;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Link className="flex w-full items-center px-0 py-2 no-underline hover:bg-background-light md:hidden" href="/products/my"><div className="flex w-8 justify-end"><Zap size={20} /></div><button className="flex whitespace-nowrap border-none bg-transparent pl-2 text-center text-base font-semibold text-textPrimary transition-all duration-200 ease-in-out">Trons</button></Link>;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== currentUser.isAuthenticated || $[31] !== currentUser.oauthProviders || $[32] !== currentUser.tag || $[33] !== currentUser.username || $[34] !== props) {
    t9 = currentUser.isAuthenticated && <><Link className="flex w-full items-center px-0 py-2 no-underline hover:bg-background-light" href={`/user/${currentUser.username}`}><div className="flex w-8 justify-end"><User width={20} height={20} color="rgba(var(--text-color))" /></div><button className="flex whitespace-nowrap border-none bg-transparent pl-2 text-center text-base font-semibold text-textPrimary transition-all duration-200 ease-in-out">Profile</button></Link><Link className="flex w-full items-center px-0 py-2 no-underline hover:bg-background-light" href="/saved"><div className="flex w-8 justify-end"><Bookmark strokeWidth={2} width={20} height={20} color="rgba(var(--text-color))" /></div><button className="flex whitespace-nowrap border-none bg-transparent pl-2 text-center text-base font-semibold text-textPrimary transition-all duration-200 ease-in-out">Saved Items</button></Link><Link className="flex w-full items-center px-0 py-2 no-underline hover:bg-background-light" href="/analytics"><div className="flex w-8 justify-end"><Activity width={20} height={20} color="rgba(var(--text-color))" /></div><button className="flex whitespace-nowrap border-none bg-transparent pl-2 text-center text-base font-semibold text-textPrimary transition-all duration-200 ease-in-out">Analytics</button></Link><Link className="flex w-full items-center px-0 py-2 no-underline hover:bg-background-light" href="/invite"><div className="flex w-8 justify-end"><PlusCircle width={20} height={20} color="rgba(var(--text-color))" /></div><button className="flex whitespace-nowrap border-none bg-transparent pl-2 text-center text-base font-semibold text-textPrimary transition-all duration-200 ease-in-out">Invite Users</button></Link>{props.isAdmin && <><button onClick={() => {
          props.setDropdownOpen(false);
          props.openNewPostModal();
        }} className="flex w-full items-center px-0 py-2 hover:bg-background-light"><div className="flex w-8 justify-end"><Bug size={16} color="rgba(var(--text-color))" /></div><span className="flex-1 pl-2 text-left text-base font-semibold text-textPrimary">Report Bug</span></button><button onClick={() => {
          props.setDropdownOpen(false);
          props.openFeatureRequestModal();
        }} className="flex w-full items-center px-0 py-2 hover:bg-background-light"><div className="flex w-8 justify-end"><Lightbulb size={16} color="rgba(var(--text-color))" /></div><span className="flex-1 pl-2 text-left text-base font-semibold text-textPrimary">Feature Request</span></button></>}{currentUser.tag === "BOT" && currentUser.oauthProviders.length === 0 && <a onClick={event => {
        event.stopPropagation();
        props.setDropdownOpen(false);
        props.openVerificationModal();
      }} className="flex w-full items-center px-0 py-2 no-underline hover:bg-background-light"><div className="flex w-8 justify-end"><ShieldCheck width={20} height={20} color="rgba(var(--text-color))" /></div><button className="flex whitespace-nowrap border-none bg-transparent pl-2 text-center text-base font-semibold text-textPrimary transition-all duration-200 ease-in-out">Verify Account</button></a>}</>;
    $[30] = currentUser.isAuthenticated;
    $[31] = currentUser.oauthProviders;
    $[32] = currentUser.tag;
    $[33] = currentUser.username;
    $[34] = props;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== currentUser.isAuthenticated || $[37] !== props) {
    t10 = !currentUser.isAuthenticated && <><a className="flex w-full items-center px-0 py-2 no-underline hover:bg-background-light" onClick={event_0 => {
        event_0.stopPropagation();
        props.setDropdownOpen(false);
        props.openLoginModal();
      }}><div className="flex w-8 justify-end text-textPrimary"><LogIn width={20} height={20} /></div><button className="flex whitespace-nowrap border-none bg-transparent pl-2 text-center text-base font-semibold text-textPrimary transition-all duration-200 ease-in-out">Login</button></a><a className="flex w-full items-center px-0 py-2 no-underline hover:bg-background-light" onClick={event_1 => {
        event_1.stopPropagation();
        props.setDropdownOpen(false);
        props.openSignupModal();
      }}><div className="flex w-8 justify-end text-textPrimary"><UserPlus width={20} height={20} /></div><button className="flex whitespace-nowrap border-none bg-transparent pl-2 text-center text-base font-semibold text-textPrimary transition-all duration-200 ease-in-out">Sign up</button></a></>;
    $[36] = currentUser.isAuthenticated;
    $[37] = props;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== effectiveTheme) {
    t11 = <div className="flex w-8 justify-end">{effectiveTheme === "light" ? <Sun width={20} height={20} color="rgba(var(--text-color))" /> : <Moon width={20} height={20} color="rgba(var(--text-color))" />}</div>;
    $[39] = effectiveTheme;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  const t12 = effectiveTheme === "light" ? "Light" : "Dark";
  let t13;
  if ($[41] !== t12) {
    t13 = <button className="flex whitespace-nowrap border-none bg-transparent pl-2 text-center text-base font-semibold text-textPrimary transition-all duration-200 ease-in-out">{t12}</button>;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== t11 || $[44] !== t13) {
    t14 = <Link className="flex w-full items-center px-0 py-2 no-underline hover:bg-background-light" href="/settings/theme">{t11}{t13}</Link>;
    $[43] = t11;
    $[44] = t13;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== currentUser.isAuthenticated || $[47] !== props) {
    t15 = currentUser.isAuthenticated && <a className="flex w-full items-center px-0 py-2 no-underline hover:bg-background-light" onClick={event_2 => {
      event_2.stopPropagation();
      props.setDropdownOpen(false);
      props.openLogoutModal();
    }}><div className="flex w-8 justify-end text-textPrimary"><LogOut width={20} height={20} /></div><button className="flex whitespace-nowrap border-none bg-transparent pl-2 text-center text-base font-semibold text-textPrimary transition-all duration-200 ease-in-out">Logout</button></a>;
    $[46] = currentUser.isAuthenticated;
    $[47] = props;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="flex flex-row flex-wrap items-center gap-x-1 gap-y-0.5 border-t border-solid border-t-borderPrimary px-2 py-2 text-xs text-textSecondary"><Link className="hover:text-textPrimary hover:underline" href="/about">About</Link><span className="text-borderPrimary">·</span><Link className="hover:text-textPrimary hover:underline" href="/credits">Credits</Link><span className="text-borderPrimary">·</span><Link className="hover:text-textPrimary hover:underline" href="/terms">Terms</Link><span className="text-borderPrimary">·</span><Link className="hover:text-textPrimary hover:underline" href="/privacy">Privacy</Link><span className="text-borderPrimary">·</span><Link className="hover:text-textPrimary hover:underline" href="/cookies">Cookies</Link><span className="text-borderPrimary">·</span><Link className="hover:text-textPrimary hover:underline" href="/eula">EULA</Link><span className="text-borderPrimary">·</span><Link className="hover:text-textPrimary hover:underline" href="/aup">AUP</Link><span className="text-borderPrimary">·</span><Link className="hover:text-textPrimary hover:underline" href="/dsar">DSAR</Link></div>;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== accountToSwitch || $[51] !== confirmSwitchId || $[52] !== props) {
    t17 = confirmSwitchId !== null && accountToSwitch && <Confirmation cancelHandler={() => setConfirmSwitchId(null)} confirmHandler={() => {
      props.onQuickSwitch(confirmSwitchId);
      setConfirmSwitchId(null);
    }} title="Switch Account" message={`Switch to @${accountToSwitch.user.username}? Your current session will be saved.`} actionButtonText="Switch" />;
    $[50] = accountToSwitch;
    $[51] = confirmSwitchId;
    $[52] = props;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] !== accountToRemove || $[55] !== confirmRemoveId || $[56] !== handleConfirmRemove || $[57] !== removingId) {
    t18 = confirmRemoveId !== null && accountToRemove && <Confirmation cancelHandler={() => setConfirmRemoveId(null)} confirmHandler={handleConfirmRemove} title="Remove Account" message={`Remove @${accountToRemove.user.username} from this device? You'll need to log in again to add it back.`} actionButtonText="Remove" variant="DANGER" actionButtonLoading={removingId !== null} />;
    $[54] = accountToRemove;
    $[55] = confirmRemoveId;
    $[56] = handleConfirmRemove;
    $[57] = removingId;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== t10 || $[60] !== t14 || $[61] !== t15 || $[62] !== t17 || $[63] !== t18 || $[64] !== t4 || $[65] !== t5 || $[66] !== t9) {
    t19 = <div className="absolute right-1 top-12 z-50 flex w-64 flex-col items-start rounded-md border border-solid border-ds-border-standard bg-background-primary px-0 shadow-shadowColor md:right-0">{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t14}{t15}{t16}{t17}{t18}</div>;
    $[59] = t10;
    $[60] = t14;
    $[61] = t15;
    $[62] = t17;
    $[63] = t18;
    $[64] = t4;
    $[65] = t5;
    $[66] = t9;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  return t19;
};
export default UserDropdown;
function _temp(open) {
  return !open;
}